const EVENT_REF_NAMES = require("../shared/eventRefNames.data");

export const DEVICE_CONNECTION_EVENT_CODE = 1048267;
export const EXCLUDED_NOTIFICATION_EVENT_TYPES = new Set([53, 54, 60, 61]);

const EVENT_REF_MAP = new Map(
  (EVENT_REF_NAMES || []).map((ref) => [Number(ref.code), ref]),
);

function resolveEventTypeSource(referenceDataEventTypes) {
  if (Array.isArray(referenceDataEventTypes)) {
    return referenceDataEventTypes;
  }

  if (Array.isArray(referenceDataEventTypes?.eventTypes)) {
    return referenceDataEventTypes.eventTypes;
  }

  return EVENT_REF_NAMES;
}

export function parseRecipientInput(value = "") {
  return String(value || "")
    .split(/[\n,;]+/g)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function formatRecipientInput(recipients = []) {
  return Array.isArray(recipients) ? recipients.join("\n") : "";
}

export function parseHeadersInput(value = "") {
  const raw = String(value || "").trim();
  if (!raw) {
    return { value: {}, error: "" };
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {
        value: {},
        error: "Headers must be a JSON object of key/value pairs.",
      };
    }

    return {
      value: Object.entries(parsed).reduce((sum, [key, headerValue]) => {
        const normalizedKey = String(key || "").trim();
        if (!normalizedKey) {
          return sum;
        }
        sum[normalizedKey] =
          headerValue == null ? "" : String(headerValue);
        return sum;
      }, {}),
      error: "",
    };
  } catch (error) {
    return {
      value: {},
      error: "Headers must be valid JSON.",
    };
  }
}

export function formatHeadersInput(headers = {}) {
  if (!headers || typeof headers !== "object" || Array.isArray(headers)) {
    return "";
  }
  const entries = Object.entries(headers);
  if (!entries.length) {
    return "";
  }
  return JSON.stringify(headers, null, 2);
}

export function getNotificationEventName(code, fallbackName = "") {
  const numericCode = Number(code);
  return (
    EVENT_REF_MAP.get(numericCode)?.name ||
    fallbackName ||
    `Code ${numericCode}`
  );
}

export function buildNotificationEventOptions(referenceDataEventTypes = null) {
  return resolveEventTypeSource(referenceDataEventTypes)
    .map((entry) => ({
      code: Number(entry.code),
      name: getNotificationEventName(entry.code, entry.name),
    }))
    .filter((entry) => Number.isFinite(entry.code))
    .filter((entry) => !EXCLUDED_NOTIFICATION_EVENT_TYPES.has(entry.code))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function buildNotificationPresetSelections(presetKey, eventOptions = []) {
  if (presetKey === "deviceOfflineOnly") {
    return {
      [DEVICE_CONNECTION_EVENT_CODE]: {
        eventType: DEVICE_CONNECTION_EVENT_CODE,
        notifyOnTriggered: true,
        notifyOnCleared: false,
      },
    };
  }

  if (presetKey === "deviceOfflineAndOnline") {
    return {
      [DEVICE_CONNECTION_EVENT_CODE]: {
        eventType: DEVICE_CONNECTION_EVENT_CODE,
        notifyOnTriggered: true,
        notifyOnCleared: true,
      },
    };
  }

  if (presetKey === "allSupportedEvents") {
    return (eventOptions || []).reduce((sum, option) => {
      sum[option.code] = {
        eventType: option.code,
        notifyOnTriggered: true,
        notifyOnCleared: option.code === DEVICE_CONNECTION_EVENT_CODE,
      };
      return sum;
    }, {});
  }

  return {};
}

export function summarizeTargets(rule = {}, referenceData = {}) {
  const deviceMap = new Map(
    (referenceData.devices || []).map((device) => [
      `device:${String(device.serial)}`,
      device.name || `PDU ${device.serial}`,
    ]),
  );
  const groupMap = new Map(
    (referenceData.groups || []).map((group) => [
      `group:${String(group.id)}`,
      group.name || `Group ${group.id}`,
    ]),
  );

  const labels = (rule.targets || [])
    .map((target) => {
      if (target.targetName) {
        return target.targetName;
      }
      const key = `${target.targetType}:${String(target.targetId)}`;
      if (deviceMap.has(key)) {
        return deviceMap.get(key);
      }
      if (groupMap.has(key)) {
        return groupMap.get(key);
      }
      return `${target.targetType}:${target.targetId}`;
    })
    .filter(Boolean);

  if (labels.length === 0) {
    return "No targets";
  }
  if (labels.length <= 2) {
    return labels.join(", ");
  }
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2} more`;
}

export function summarizeDestinations(rule = {}) {
  const destinations = Array.isArray(rule.destinations) ? rule.destinations : [];
  const emailCount = destinations.filter((item) => item.channel === "email").length;
  const webhookCount = destinations.filter((item) => item.channel === "webhook").length;
  const parts = [];
  if (emailCount) {
    parts.push(`${emailCount} email`);
  }
  if (webhookCount) {
    parts.push(`${webhookCount} webhook`);
  }
  return parts.length ? parts.join(", ") : "No destinations";
}

export function summarizeEventSelections(rule = {}, referenceData = {}) {
  const selections = Array.isArray(rule.eventSelections)
    ? rule.eventSelections
    : [];
  const eventOptions = buildNotificationEventOptions(referenceData);

  if (
    selections.length &&
    eventOptions.length &&
    selections.length === eventOptions.length &&
    selections.every(
      (selection) =>
        selection.notifyOnTriggered &&
        (selection.eventType === DEVICE_CONNECTION_EVENT_CODE
          ? selection.notifyOnCleared
          : !selection.notifyOnCleared),
    )
  ) {
    return "All supported events";
  }

  if (
    selections.length === 1 &&
    Number(selections[0].eventType) === DEVICE_CONNECTION_EVENT_CODE &&
    selections[0].notifyOnTriggered &&
    !selections[0].notifyOnCleared
  ) {
    return "Device offline only";
  }

  if (
    selections.length === 1 &&
    Number(selections[0].eventType) === DEVICE_CONNECTION_EVENT_CODE &&
    selections[0].notifyOnTriggered &&
    selections[0].notifyOnCleared
  ) {
    return "Device offline + online";
  }

  if (!selections.length) {
    return "No events";
  }

  if (selections.length <= 2) {
    return selections
      .map((selection) =>
        selection.eventName || getNotificationEventName(selection.eventType),
      )
      .join(", ");
  }

  return `${selections.length} event types`;
}
